import { CommonMessage } from '@frontend/lang';
import { reportBug } from '@frontend/sentry';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';

type ReportState = 'idle' | 'open' | 'submitting' | 'success' | 'error';

const WizardScreenNotFound = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const [reportState, setReportState] = useState<ReportState>('idle');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setReportState('submitting');
        const result = await reportBug({ message, name, email });
        setReportState(result ? 'success' : 'error');
    }

    useWizardModalScreenNavigation({
        onContinue: () => navigate('/'),
        continueDisabled: false,
        continueLabel: CommonMessage.BUTTONS.HOME
    });

    return (
        <div
            id='wizard-screen-not-found'
            className='flex flex-col gap-6 overflow-y-auto'>
            <div className='outline-border-default flex w-full flex-col items-center gap-4 rounded-xl p-6 text-center outline-1'>
                <div className='bg-status-error/10 flex size-14 items-center justify-center rounded-full'>
                    <svg
                        className='text-status-error size-7'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth={2}>
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
                        />
                    </svg>
                </div>

                <div className='flex flex-col gap-1'>
                    <h2 className='text-foreground-default text-lg font-semibold'>
                        <FormattedMessage
                            id='WizardScreenNotFound.Heading'
                            description='Heading shown when a wizard navigates to an unsupported screen type'
                            defaultMessage='Something went wrong'
                        />
                    </h2>
                    <p className='text-brand-muted text-sm'>
                        <FormattedMessage
                            id='WizardScreenNotFound.Description'
                            description='Explanation shown when a wizard navigates to an unsupported screen type, prompting the user to go back or home'
                            defaultMessage='The page you are trying to access could not be found. Please go back or return home.'
                        />
                    </p>
                </div>
            </div>

            {reportState === 'success' && (
                <div className='bg-status-success/10 text-status-success w-full rounded-xl p-4 text-center text-sm font-medium'>
                    {CommonMessage.ERRORS.BUG_REPORT.FORM_SUCCESS((chunks) => (
                        <strong>{chunks}</strong>
                    ))}
                </div>
            )}

            {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                <form
                    onSubmit={handleSubmit}
                    className='flex w-full flex-col gap-3'>
                    <div className='flex flex-col gap-1'>
                        <label className='text-foreground-default text-sm font-medium'>{CommonMessage.FORMS.NAME}</label>
                        <input
                            type='text'
                            className='border-border-default bg-background-surface text-foreground-default w-full rounded-md border px-3 py-2 text-sm outline-none disabled:opacity-50'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={intl.formatMessage({
                                id: 'SentryErrorBoundary.Form.NamePlaceholder',
                                description: 'Placeholder for the name field in the bug report form',
                                defaultMessage: 'Your name (optional)'
                            })}
                            disabled={reportState === 'submitting'}
                        />
                    </div>
                    <div className='flex flex-col gap-1'>
                        <label className='text-foreground-default text-sm font-medium'>{CommonMessage.ERRORS.BUG_REPORT.FORM_EMAIL_LABEL}</label>
                        <input
                            type='email'
                            className='border-border-default bg-background-surface text-foreground-default w-full rounded-md border px-3 py-2 text-sm outline-none disabled:opacity-50'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={intl.formatMessage({
                                id: 'SentryErrorBoundary.Form.EmailPlaceholder',
                                description: 'Placeholder for the email field in the bug report form',
                                defaultMessage: 'your@email.com (optional)'
                            })}
                            disabled={reportState === 'submitting'}
                        />
                    </div>
                    <div className='flex flex-col gap-1'>
                        <label className='text-foreground-default text-sm font-medium'>
                            {CommonMessage.ERRORS.BUG_REPORT.FORM_MESSAGE_LABEL} <span className='text-status-error'>*</span>
                        </label>
                        <textarea
                            className='border-border-default bg-background-surface text-foreground-default w-full rounded-md border px-3 py-2 text-sm outline-none disabled:opacity-50'
                            rows={3}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={intl.formatMessage({
                                id: 'SentryErrorBoundary.Form.MessagePlaceholder',
                                description: 'Placeholder for the message field in the bug report form',
                                defaultMessage: 'Describe what you were doing when the error occurred...'
                            })}
                            required
                            disabled={reportState === 'submitting'}
                        />
                    </div>

                    {reportState === 'error' && <p className='text-status-error text-sm'>{CommonMessage.ERRORS.BUG_REPORT.FORM_ERROR}</p>}

                    <div className='flex items-center gap-3'>
                        <button
                            type='submit'
                            disabled={reportState === 'submitting' || !message.trim()}
                            className='bg-brand-primary text-always-white disabled:bg-brand-disabled disabled:text-brand-muted flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed'>
                            {reportState === 'submitting' && (
                                <span className='border-background-surface size-3.5 animate-spin rounded-full border-2 border-t-transparent' />
                            )}
                            {reportState === 'submitting' ? CommonMessage.ERRORS.BUG_REPORT.FORM_SENDING : CommonMessage.ERRORS.BUG_REPORT.FORM_SUBMIT}
                        </button>
                        <button
                            type='button'
                            className='text-brand-primary text-sm underline disabled:opacity-50'
                            onClick={() => setReportState('idle')}
                            disabled={reportState === 'submitting'}>
                            {CommonMessage.BUTTONS.CANCEL}
                        </button>
                    </div>
                </form>
            )}

            {reportState === 'idle' && (
                <button
                    type='button'
                    className='border-border-default text-foreground-default w-full rounded-md border px-4 py-2 text-sm font-medium'
                    onClick={() => setReportState('open')}>
                    {CommonMessage.ERRORS.BUG_REPORT.BUTTON}
                </button>
            )}
        </div>
    );
};

export default WizardScreenNotFound;
